import React from 'react';
import {View} from 'react-native';
import {Image} from '@rn-vui/themed';
import {observer} from 'mobx-react';

import {
  BANNER_ASPECT_RATIO_MOBILE,
  BANNER_ASPECT_RATIO_WEB,
  BANNER_DESKTOP_MAX_HEIGHT,
  BANNER_DESKTOP_MAX_WIDTH,
} from '../../../components/HomepageCarousel/constants';
import useStorePriceDisplayConditions from '../../../hooks/usePriceDisplayConditions';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {useStores} from '../../../stores/use-stores';
import {brandRewardsCopy} from '../../../utils/rewards';
import {MemberPriceCarousel} from '../../ProductCarousel';
import TitleLine from '../TitleLine';

type JustForYouProps = {
  onSeeAllPress?: () => void;
};

function JustForYou({onSeeAllPress}: JustForYouProps) {
  const {loyaltyInformationStore, shopSelectionStore} = useStores();
  const {showMemberPrice} = useStorePriceDisplayConditions();

  const {isWebDesktopScreen} = useWebScreenDimensions();

  // Non-ecommerce stores never surface product tiles — hide the whole block.
  if (shopSelectionStore.ecommerceDisabled) {
    return null;
  }

  // DIS-1590: a `private` tier is a targeted personal price, and MFL does NOT gate it
  // by session — `/ecommerce/search?member_price=private` returns the same lines to a
  // logged-out caller (measured 2026-09-02, DEV SipnSave at TEST-SNS-LOYALTY). The
  // entitlement gate is therefore the app's alone, and it belongs at the section: the
  // tiles inside gate their badge on this same `privatePricing`, so without it here the
  // section rendered targeted products stripped of the member story, with the sale
  // presentation ("was $57.99", MFL's SAVE sticker) showing through instead.
  //
  // MembersPriceListScreen — the "see all" destination and a linkable route — gates on
  // this same `privatePricing` during render (MembersPriceListScreen/index.tsx:31-52),
  // so the deep link cannot route around this section's gate.
  if (!showMemberPrice.privatePricing) {
    return null;
  }

  const imageUri = isWebDesktopScreen
    ? loyaltyInformationStore.information.RewardsPageJustForYou.data?.imageDesktop?.url
    : loyaltyInformationStore.information.RewardsPageJustForYou.data?.image?.url;

  return (
    <View>
      <TitleLine
        title={`${brandRewardsCopy.memberPrivatePricingTitle.toUpperCase()}`}
        onSeeAllPress={onSeeAllPress}
      />
      <Image
        onPress={onSeeAllPress}
        containerStyle={{
          marginBottom: isWebDesktopScreen ? 24 : 10,
          width: '100%',
          aspectRatio: isWebDesktopScreen ? BANNER_ASPECT_RATIO_WEB : BANNER_ASPECT_RATIO_MOBILE,
          flex: 1,
          borderRadius: 10,
          ...(isWebDesktopScreen
            ? {
                maxHeight: BANNER_DESKTOP_MAX_HEIGHT,
                maxWidth: BANNER_DESKTOP_MAX_WIDTH,
                alignSelf: 'center',
              }
            : {}),
        }}
        source={{uri: imageUri}}
      />
      <MemberPriceCarousel member_price="private" />
    </View>
  );
}

export default observer(JustForYou);
